import { useSandpack } from "@codesandbox/sandpack-react";
import * as Icons from "./Icons";
import React from "react";

function FileTree(props) {
  const { sandpack } = useSandpack();
  const { files, setActiveFile } = sandpack;

  let prefixedPath = "/";
  const filesToShow = [];

  const fileListWithoutPrefix = Object.keys(files)
    .filter((file) => file.startsWith(prefixedPath))
    .map((file) => file.substring(prefixedPath.length));

  const directoriesToShow = new Set(
    fileListWithoutPrefix
      .filter((file) => file.includes("/"))
      .map((file) => {
        filesToShow.push({
          id: makeid(5),
          name: file.split("/")[1],
          directory: `${file.split("/")[0]}`,
          type: "file",
        });

        return {
          id: `${file.split("/")[0]}`,
          name: file.split("/")[0],
          directory: null,
          type: "directory",
        };
      })
  );

  const rootFiles = fileListWithoutPrefix
    .filter((file) => !file.includes("/"))
    .map((file) => ({
      id: makeid(5),
      name: `${file}`,
      directory: null,
      type: "file",
    }));

  const parsedFiles = [...rootFiles, ...filesToShow, ...directoriesToShow];

  const onSelect = (file) => {
    let dict = file.directory == null ? "" : `/${file.directory}`;
    let update = `${dict}/${file.name}`;
    setActiveFile(update);
    props.changeFile();
  };

  return (
    <SubTree
      allFiles={parsedFiles}
      files={parsedFiles}
      onSelect={onSelect}
      {...props}
    />
  );
}

function SubTree({ files, allFiles, selectedFile, onSelect, ...props }) {
  return (
    <div {...props}>
      {files
        .filter((child) => isRootLevel(files, child))
        .sort(sortingFunction)
        .map((child) => (
          <React.Fragment key={child.id}>
            {child.type === "directory" ? (
              <Directory
                files={files}
                allFiles={allFiles}
                selectedFile={selectedFile}
                onSelect={onSelect}
                {...child}
              />
            ) : (
              <File
                selectedFile={selectedFile}
                allFiles={allFiles}
                onClick={(_) => onSelect(child)}
                {...child}
              />
            )}
          </React.Fragment>
        ))}
    </div>
  );
}

function Directory(props) {
  const children = props.allFiles.filter((file) => file.directory === props.id);

  const defaultOpen = isChildSelected({
    allFiles: props.allFiles,
    directory: props,
    selectedFile: props.selectedFile,
  });

  const [open, setOpen] = React.useState(defaultOpen);
  const toggle = () => setOpen(!open);

  return (
    <>
      <File
        icon="ClosedDirectory"
        selectedFile={props.selectedFile}
        allFiles={props.allFiles}
        onClick={toggle}
        {...props}
      />
      {open ? (
        <SubTree
          files={children}
          allFiles={props.allFiles}
          selectedFile={props.selectedFile}
          onSelect={props.onSelect}
        />
      ) : null}
    </>
  );
}

function FileIcon({ name, extension }) {
  const Icon = Icons[extension] || Icons[name];
  return (
    <span
      style={{
        display: "flex",
        width: 24,
        height: 32,
        justifyContent: "center",
        alignItems: "center",
        marginRight: 8,
      }}
    >
      <Icon />
    </span>
  );
}

const makeid = (length) => {
  var result = [];
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result.push(
      characters.charAt(Math.floor(Math.random() * charactersLength))
    );
  }
  return result.join("");
};

function File({ allFiles, files, id, selectedFile, ...props }) {
  const isSelected = selectedFile && selectedFile.id === id;
  const depth = getDepth(allFiles, props);

  return (
    <div
      {...props}
      className="fileItem"
      style={{
        display: "flex",
        alignItems: "center",
        padding: "0.2rem 1rem 0.2rem 1rem",
        marginLeft: `${depth * 1}rem`,
        cursor: "default",
      }}
    >
      <FileIcon
        name={props.icon || "File"}
        extension={props.name.split(".").pop()}
      />
      <span style={{ marginLeft: 1 }}>{props.name}</span>
    </div>
  );
}

function sortingFunction(a, b) {
  // directories come first, sorted alphabetically
  // then files, also sorted alphabetically
  let first;

  if (a.type === b.type) {
    if (a.name < b.name) first = a;
    else first = b;
  } else if (a.type === "directory") {
    first = a;
  } else {
    first = b;
  }

  // js be weird
  if (first === a) return -1;
  else return 1;
}

function isRootLevel(files, file) {
  // find out if parent directory is in sub-tree

  const parentId = file.directory;
  if (!parentId) return true;

  const parent = files.find((file) => file.id === parentId);
  if (!parent) return true;
}

function getDepth(allFiles, file) {
  let depth = 0;

  let parent = getParentDirectory(allFiles, file);

  while (parent) {
    depth++;
    parent = getParentDirectory(allFiles, parent);
  }

  return depth;
}

function isChildSelected({ allFiles, directory, selectedFile }) {
  const filesInCurrentSubTree = getFilesInSubTree(allFiles, selectedFile);

  return filesInCurrentSubTree.find((file) => file.id === directory.id);
}

function getFilesInSubTree(allFiles, selectedFile) {
  if (!selectedFile) return [];
  const currentModuleTree = [selectedFile];

  let parentDirectory = getParentDirectory(allFiles, selectedFile);

  while (parentDirectory) {
    currentModuleTree.push(parentDirectory);
    // get parent directory of the parent directory
    parentDirectory = getParentDirectory(allFiles, parentDirectory);
  }

  return currentModuleTree;
}

function getParentDirectory(allFiles, file) {
  if (file.directory) {
    return allFiles.find((parent) => parent.id === file.directory);
  }
}

export default FileTree;
